import React from "react";
import AlteredLabel, { ALTERED_THEME_ICONS } from "../../../components/AlteredLabel";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import {
  AlteredKind,
  MODIFIER_STAT_KEYS,
  MODIFIER_TARGET_ITEM,
  MODIFIER_TARGET_TROOP,
  StatModifierRule,
  alteredLabelKey,
  formatModifierPercent,
  modifierRulesFor,
} from "../../../utils/statModifiers";
import { useBattleContext } from "../../context";

type Target = {
  kind: AlteredKind;
  targetId?: number;
  // The chosen level; leave it out for a catalog entry (any level).
  level?: number;
};

function useRules({ kind, targetId, level }: Target): StatModifierRule[] {
  const { battleState } = useBattleContext();
  const type = kind === "item" ? MODIFIER_TARGET_ITEM : MODIFIER_TARGET_TROOP;
  return modifierRulesFor(battleState.statModifierRules, type, targetId, level);
}

// "Damage +50%, Health −40%", for a tooltip.
function useSummary(rules: StatModifierRule[]) {
  const { t } = useAppTranslation(["barracks"]);
  return rules
    .map((rule) => `${t(MODIFIER_STAT_KEYS[rule.stat] ?? "")} ${formatModifierPercent(rule.percent)}`)
    .join(", ");
}

/**
 * The event label and one chip per changed stat, for the troop chosen in the
 * formation preview. Renders nothing when no modifier changes it.
 */
export const FormationAlteredInfo = (target: Target) => {
  const { t } = useAppTranslation(["barracks"]);
  const rules = useRules(target);
  if (rules.length === 0) return null;
  return (
    <div className="formation-altered">
      <AlteredLabel
        theme={rules[0].theme}
        kind={target.kind}
        className="formation-altered__label"
      />
      <div className="formation-altered__chips">
        {rules.map((rule, index) => (
          <span
            key={index}
            className={
              "formation-altered__chip " +
              (rule.percent > 0 ? "formation-altered__chip--up" : "formation-altered__chip--down")
            }
          >
            {t(MODIFIER_STAT_KEYS[rule.stat] ?? "")} {formatModifierPercent(rule.percent)}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * A small event mark in the corner of a troop icon or item slot that a modifier
 * changes, with the changes as its tooltip.
 */
export const AlteredMarker = (target: Target) => {
  const { t } = useAppTranslation(["barracks"]);
  const rules = useRules(target);
  const summary = useSummary(rules);
  if (rules.length === 0) return null;
  const theme = rules[0].theme?.toLowerCase();
  const labelKey = alteredLabelKey(theme, target.kind);
  const title = (labelKey ? t(labelKey) + " — " : "") + summary;
  const icon = theme ? ALTERED_THEME_ICONS[theme] : undefined;
  return (
    <span className="formation-altered-marker" title={title} aria-label={title}>
      {icon ? <img src={icon} alt="" width={18} height={18} /> : "✦"}
    </span>
  );
};
